import { IsDefined, Validate } from 'class-validator';

import { EntityModel } from 'src/core/services/data/models';
import { OData, $data } from 'src/core/services/data/JayDataContext';
import { ContextService } from 'src/core/services/context.service';
import { DataService, property } from 'src/core/services/data.service';
import { BookemZulu as Model } from 'src/core/services/data/JayDataContext';
import { DisplayNameValidation } from 'src/core/customValidator/displayNameValidator';
import { ContractStructureValidation } from 'src/core/customValidator/contractStructureValidator';
import { ObjectUtils } from 'src/core/utils/object';

export class ContractDataService extends DataService<typeof Model.Products.Logic.ViewModels.ContractItem, Model.Products.Logic.ViewModels.ContractItem, ContractItem> {
	id: number = -1;

	constructor(contextService: ContextService, entitySet: (context: OData.Container) => $data.EntitySet<typeof Model.Products.Logic.ViewModels.ContractItem, Model.Products.Logic.ViewModels.ContractItem>) {
		super(contextService, entitySet);
	}
	protected map(entity: Model.Products.Logic.ViewModels.ContractItem): ContractItem {
		return new ContractItemModel(entity);
	}

	static make(context?: any): ContractDataService {
		return new ContractDataService(context, ctx => ctx.Contracts);
	}

	add(product: ProductItem | TourItineraryItem, defaultLanguaje: string): Promise<ContractItem> {
		let priceType = "PerProduct";
		const entity = new Model.Products.Logic.ViewModels.ContractItem();
		if (product.entity.getType() == Model.Products.Logic.ViewModels.ProductItem) {
			entity.productId = product.id;
			switch ((product as ProductItem).productType) {
				case 1:
					priceType = 'PerNight';
					break;
				case 2:
					priceType = 'PerDay';
					break;
				case 3:
				case 4:
				case 9:
					priceType = 'PerPerson';
					break;
			}
		} else {
			entity.tourItineraryId = product.id;
		}
		entity.id = this.id--;
		const ageBands = product.dynamicProperties.ageBands || [];
		const customOccupancyConfigs = product.dynamicProperties.customOccupancyConfigs || [];
		(<any>entity).dynamicProperties = {
			displayName: {
				[defaultLanguaje]: ''
			},
			erratas: [],
			ageBands: ObjectUtils.deepCopy(ageBands, (prop) => prop === "__isProxy") || [],
			cost: {
				structure: {
					type: priceType,
					required: true,
					replaceBasePrice: false,
					mode: 'Amount',
					sign: 'Up',
					crossingDateBandBehaviour: 'AddTogether',
					maxPax: product.dynamicProperties.maxPax,
					isCustomOccupancyConfig: customOccupancyConfigs.length > 0,
					customOccupancyConfigs: ObjectUtils.deepCopy(customOccupancyConfigs, (prop) => prop === "__isProxy")
				},
				elements: []
			},
			price: {
				structure: {
					type: priceType,
					required: true,
					replaceBasePrice: false,
					mode: 'Amount',
					sign: 'Up',
					crossingDateBandBehaviour: 'AddTogether',
					defaultMargin: 0.25,
					customOccupancyConfigs: ObjectUtils.deepCopy(customOccupancyConfigs, (prop) => prop === "__isProxy"),
					maxPax: product.dynamicProperties.maxPax,
					isCustomOccupancyConfig: customOccupancyConfigs.length > 0,
					currencyExchangeRate: 1
				},
				elements: []
			},
			isCommissionable: true
		};

		return this.create(() => entity);
	}

	clone(contract: ContractItem): any {
		let contractClone = {
			id: (contract.id > 0) ? contract.id * -1 : contract.id - 100,
			productId: contract.productId,
			dynamicProperties: ObjectUtils.deepCopy(contract.dynamicProperties, attr => attr == '__isProxy' || attr == '__edit'),
			supplierId: contract.supplierId,
			isPublished: contract.isPublished,
			product: contract.product,
			allotmentId: contract.allotmentId,
			dateBands: [],
			services: [],
			markets: []
		};

		return contractClone;
	}
}

export class ContractItemModel extends EntityModel<Model.Products.Logic.ViewModels.ContractItem, ContractItem> implements ContractItem {
	@property<ContractItemModel>(o => o.entity.id, (o, value) => o.entity.id = value)
	public id: number;

	@property<ContractItemModel>(o => o.entity.supplierId, (o, value) => o.entity.supplierId = value)
	@IsDefined({ message: 'Supplier is required' })
	public supplierId: number;

	@property<ContractItemModel>(o => o.entity.supplierTitle, (o, value) => o.entity.supplierTitle = value)
	public supplierTitle: string;
	@property<ContractItemModel>(o => o.entity.productId, (o, value) => o.entity.productId = value)
	public productId: number;

	//@property<ContractItemModel>(o => o.entity.product, (o, value) => o.entity.product = value)
	public product: any;

	@property<ContractItemModel>(o => o.entity.tourItineraryId, (o, value) => o.entity.tourItineraryId = value)
	public tourItineraryId: number;

	//@property<ContractItemModel>(o => o.entity.tourItinerary, (o, value) => o.entity.tourItinerary = value)
	public tourItinerary: any;

	@property<any>(o => o.entity.dynamicProperties, (o, value) => o.entity.dynamicProperties = value)
	@Validate(DisplayNameValidation, ['en'])
	@Validate(ContractStructureValidation)
	public dynamicProperties: {};

	@property<ContractItemModel>(o => o.entity.dateBands, (o, value) => o.entity.dateBands = value)
	public dateBands: ContractRangeDateItem[];

	@property<ContractItemModel>(o => o.entity.services, (o, value) => o.entity.services = value)
	public services: ContractServiceTypeItem[];

	@property<ContractItemModel>(o => o.entity.markets, (o, value) => o.entity.markets = value)
	public markets: ContractMarketItem[];

	@property<ContractItemModel>(o => o.entity.isPublished, (o, value) => o.entity.isPublished = value)
	public isPublished: boolean;

	@property<ContractItemModel>(o => o.entity.allotmentId, (o, value) => o.entity.allotmentId = value)

	public allotmentId: number;

	@property<ContractItemModel>(o => o.entity.allotment, (o, value) => o.entity.allotment = value)

	public allotment: AllotmentItem;
}



// WEBPACK FOOTER //
// ./src/app/products/contract/form/contract-form.data.ts